import React, {
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useState,
} from 'react';
import { notification } from 'antd';

import Auth0UserDetailsContext from '@totem/components/services/remoteAccess/auth0users/details/Auth0UserDetailsContext';
import {
  Auth0UserItem,
  IBAuth0RoleSummary,
  RemoteAccessUserDetails,
} from '@totem/components/services/remoteAccess/auth0users/details/types';
import UserProfileContext from '@totem/components/userContext/UserProfileContext';
import { AUTH0_USER_ENDPOINT } from '@totem/utilities/endpoints';

type Props = {
  email: string;
  children?: ReactNode;
};

const Auth0UserDetailsContainer = ({ email, children }: Props) => {
  const { getApiToken } = useContext(UserProfileContext);
  const [loading, setLoading] = useState<boolean>(true);
  const [user, setUser] = useState<Auth0UserItem | null>(null);
  const [roles, setRoles] = useState<IBAuth0RoleSummary[]>([]);
  const [refreshData, setRefreshData] = useState<boolean>(true);

  useEffect(() => {
    if (!refreshData || !email) {
      return;
    }

    setRefreshData(false);

    setLoading(true);

    fetch(`${AUTH0_USER_ENDPOINT}/${encodeURIComponent(email)}`, {
      method: 'GET',
      headers: new Headers({
        Authorization: `Bearer ${getApiToken()}`,
      }),
    })
      .then((res) => res.json())
      .then((result: RemoteAccessUserDetails) => {
        setUser(result?.user ?? null);

        setRoles(result?.roles ?? []);
      })
      .catch(() => {
        notification.error({ message: 'Failed to load user details.' });
      })
      .finally(() => setLoading(false));
  }, [refreshData, email, getApiToken]);

  const onRefresh = useCallback(() => {
    setRefreshData(true);
  }, []);

  return (
    <Auth0UserDetailsContext.Provider
      value={{ loading, user, roles, onRefresh }}
    >
      {children}
    </Auth0UserDetailsContext.Provider>
  );
};

export default Auth0UserDetailsContainer;
